import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import {
  Tabs,
  TabsTrigger,
  TabsListWithIndicator,
  TabsContent,
} from "@/core/components/ui/tabs";
import { toast } from "@/core/components/ui/toast";
import {
  HeaderStack,
  MainCard,
  MainStack,
} from "@/business-settings/common/business-settings-page";
import { PageHeader } from "@/business-settings/common/page-header";
import { SIDEBAR_MODES } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import {
  useGetMyUserWithBusiness,
  useGetWebAppUsers,
  useGetTechnicians,
} from "@/user/query";
import { isAdminOrOwner } from "@/user/utils";
import { Technicians } from "./technicians";
import { WebAppUsers } from "./web-app-users";
import { DeleteUserConfirmationModal } from "./web-app-users/delete-user-confirmation-modal";

const Users = () => {
  const { updateSidebar } = useBusinessSettingsSidebar();
  const navigate = useNavigate();
  const { businessId, tab } = useParams();

  const { data: userInfo } = useGetMyUserWithBusiness();
  const { data: webAppUsers = [], isLoading: isLoadingWebAppUsers } =
    useGetWebAppUsers(businessId);
  const { data: technicians = [], isLoading: isLoadingTechnicians } =
    useGetTechnicians(businessId);
  const isOwnerOrAdminUser = isAdminOrOwner(userInfo?.role);

  const allUsers = [...webAppUsers, ...technicians];

  const activeTab = tab ?? "web-app-users";

  const handleNewUser = () => {
    if (!isAdminOrOwner(userInfo?.role)) {
      toast("You are not authorized to create users");
      return;
    }
    updateSidebar({
      section: activeTab === "web-app-users" ? "users" : "technicians",
      mode: SIDEBAR_MODES.new,
      selectedItemId: undefined,
      isOpen: true,
    });
  };

  const handleTabChange = (tab: string) => {
    updateSidebar({
      selectedItemId: undefined,
      mode: SIDEBAR_MODES.view,
      section: tab === "web-app-users" ? "users" : "technicians",
      isOpen: false,
    });
    navigate(`/business-settings/${businessId}/users/${tab}`);
  };

  return (
    <>
      <Stack className="mr-5" gap="sm" fullWidth>
        <HeaderStack>
          <PageHeader>Users</PageHeader>
          {isOwnerOrAdminUser && (
            <Button variant="secondary" size="sm" onClick={handleNewUser}>
              New {activeTab === "web-app-users" ? "user" : "technician"}
            </Button>
          )}
        </HeaderStack>
        <MainCard>
          <MainStack>
            <Tabs
              value={activeTab}
              onValueChange={handleTabChange}
              className="flex w-full flex-1 flex-col overflow-hidden"
            >
              <TabsListWithIndicator
                className="w-full justify-start"
                containerClassName="mr-5"
              >
                <TabsTrigger
                  count={webAppUsers.length}
                  value="web-app-users"
                  isCountLoading={isLoadingWebAppUsers}
                >
                  Web App Users
                </TabsTrigger>
                <TabsTrigger
                  count={technicians.length}
                  value="technicians"
                  isCountLoading={isLoadingTechnicians}
                >
                  Technicians
                </TabsTrigger>
              </TabsListWithIndicator>
              <TabsContent
                value="web-app-users"
                className="flex-1 overflow-hidden pt-8"
              >
                <WebAppUsers />
              </TabsContent>
              <TabsContent
                value="technicians"
                className="flex-1 overflow-hidden pt-8"
              >
                <Technicians />
              </TabsContent>
            </Tabs>
          </MainStack>
        </MainCard>
      </Stack>
      <DeleteUserConfirmationModal users={allUsers} />
    </>
  );
};

export default Users;
